import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
// import { ko } from 'vuetify/locale'  // 로케일 임포트 제거
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import i18n from './i18n'

// FormKit 설정
import { plugin as formKitPlugin, defaultConfig } from '@formkit/vue'
import { generateClasses } from '@formkit/themes'

// 기본 FormKit 클래스 정의
const formKitClasses = {
  global: {
    wrapper: 'mb-3',
    label: 'block font-bold mb-1',
    input: 'w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary',
    help: 'text-xs text-gray-500 mt-1',
    messages: 'list-none p-0 mt-1 mb-0',
    message: 'text-red-500 text-xs'
  }
}

// Vuetify 설정 - 로케일 설정 제거
const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: 'light',
    themes: {
      light: {
        dark: false,
        colors: {
          primary: '#171c61',    // 평창군 메인 네이비색
          secondary: '#2d3692',  // 밝은 네이비색
          accent: '#5a63c4',     // 퍼플-블루색
          error: '#f44336',      // 빨간색
          info: '#2196F3',       // 파란색
          success: '#4caf50',    // 그린
          warning: '#FF9800',    // 주황색
          background: '#f5f6fb', // 아주 연한 블루-그레이 배경
          surface: '#FFFFFF',    // 흰색 서피스
          'on-primary': '#FFFFFF', // 주 색상 위 텍스트
          'on-secondary': '#FFFFFF', // 보조 색상 위 텍스트
          'primary-darken-1': '#1a1f54', // 더 어두운 네이비색
          'primary-lighten-1': '#8a91d8', // 밝은 퍼플-블루색
        },
      },
      dark: {
        dark: true,
        colors: {
          primary: '#171c61',
          secondary: '#2d3692',
          accent: '#5a63c4',
          error: '#ff5252',
          info: '#2196F3',
          success: '#4caf50',
          warning: '#FF9800',
          background: '#1a1a1a',
          surface: '#2c2c2c',
        },
      },
    },
  },
})

// Pinia 상태 관리 설정
const pinia = createPinia()

// 방어적 코드: 오류 핸들링 함수
const errorHandler = (err, vm, info) => {
  // 무시할 오류 목록
  const ignoreErrors = [
    'translateDisabled',
    'Script error',
    'ResizeObserver loop',
    'ResizeObserver loop completed',
    'ResizeObserver loop limit exceeded',
    'ethereum',
    'undefined is not an object'
  ];
  
  // 오류 메시지 추출
  let errorMessage = '';
  try {
    if (err && err.toString) {
      errorMessage = err.toString();
    } else {
      errorMessage = String(err);
    }
  } catch (e) {
    errorMessage = 'Unknown error';
  }
  
  // 무시해야 할 오류인지 확인
  for (const ignorePattern of ignoreErrors) {
    if (errorMessage.includes(ignorePattern)) {
      console.log(`무시된 Vue 오류: ${errorMessage}`);
      return; // 오류 무시
    }
  }
  
  // 무시되지 않은 오류는 기록
  console.error('Vue 오류:', errorMessage);
  
  // 오류 기록에 추가
  if (window._errors) {
    window._errors.push({
      type: 'vue-error',
      message: errorMessage,
      info: info,
      timestamp: new Date().toISOString()
    });
  }
};

// iOS 기기 감지
const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

// iOS 기기일 경우 추가 대응
if (isIOS || isSafari) {
  // iOS 기기를 위한 클래스 추가
  document.body.classList.add('ios-device');
  
  // iOS에서 번역 관련 오류 억제
  if (typeof window.translateDisabled === 'undefined') {
    window.translateDisabled = true;
  }
  
  // iOS에서 ethereum 관련 오류 억제
  if (typeof window.ethereum === 'undefined') {
    window.ethereum = {
      selectedAddress: null,
      isMetaMask: false,
      on: function() {},
      request: function() { return Promise.resolve(null); }
    };
  }
}

// 애플리케이션 생성 및 초기화
const app = createApp(App)

// 오류 핸들러 설정
app.config.errorHandler = errorHandler
// eslint-disable-next-line no-unused-vars
app.config.warnHandler = (msg, vm, trace) => {
  // 무시할 경고 목록
  const ignoreWarnings = [
    'translate',
    'i18n'
  ];
  
  // 무시해야 할 경고인지 확인
  for (const ignorePattern of ignoreWarnings) {
    if (msg.includes(ignorePattern)) {
      return; // 경고 무시
    }
  }
  
  console.warn(`Vue 경고: ${msg}`);
};

// 플러그인 등록
app.use(router)
app.use(pinia)
app.use(vuetify)
app.use(i18n)
app.use(formKitPlugin, defaultConfig({
  config: {
    classes: generateClasses(formKitClasses)
  }
}))

// 앱 마운트
app.mount('#app')
